import { httpServiceClient } from '@/infra/http'
import { makeApiUrl } from '@/main/factories/http/api-url-factory'
import { formatCollaboratorList, formatFilterOptions } from './collaborator-service-utils'
import {
  CollaboratorFilterParams,
  CollaboratorListResponse,
  CollaboratorListItem,
  FilterOptions,
  AllPagesParams
} from './collaborator-service-types'

/**
 * Collaborator Service
 *
 * IMPORTANT: Uses OLD backoffice endpoints (AS-IS migration from skillhub-web)
 * DO NOT change these endpoints without verifying with backend team
 */
export const createCollaboratorService = (client = httpServiceClient) => ({
  /**
   * Fetch paginated list of collaborators
   * OLD ENDPOINT: GET /api/user/
   * @param _companyId - Not used in old API, kept for interface compatibility
   */
  async fetchList(
    _companyId: string,
    params: CollaboratorFilterParams,
    signal?: AbortSignal
  ): Promise<CollaboratorListResponse> {
    const response = await client<CollaboratorListResponse>({
      method: 'get',
      url: makeApiUrl('/user/'),
      params: {
        page: params.page,
        searchText: params.searchText || undefined,
        companies: params.companies || undefined,
        departments: params.departments || undefined,
        jobs: params.jobs || undefined,
        userTest: params.userTest || false,
        companyTest: params.companyTest || false,
        onboardingComplete:
          params.onboardingComplete !== undefined ? params.onboardingComplete : undefined,
        accountType: params.accountType || undefined
      },
      signal
    })

    return formatCollaboratorList(response.body.data as CollaboratorListResponse)
  },

  /**
   * Fetch all pages for bulk selection
   * OLD ENDPOINT: POST /api/user/get-all-pages
   */
  async fetchAllPages(params: AllPagesParams): Promise<{ results: CollaboratorListItem[] }> {
    const response = await client<{ results: CollaboratorListItem[] }>({
      method: 'post',
      url: makeApiUrl('/user/get-all-pages'),
      body: {
        page: params.totalPages,
        searchText: params.searchText || '',
        expireDate: '',
        companiesId: params.companiesId || [],
        departments: params.departments || [],
        jobs: params.jobs || [],
        userTest: params.userTest || false,
        companyTest: params.companyTest || false,
        onboardingComplete:
          params.onboardingComplete !== undefined ? params.onboardingComplete : null,
        accountType: params.accountType || null
      }
    })

    return response.body.data as { results: CollaboratorListItem[] }
  },

  /**
   * Fetch filter options (companies, departments, jobs, account types, etc.)
   * OLD ENDPOINT: GET /api/management/options
   * @param _companyId - Not used in old API, kept for interface compatibility
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async fetchFilterOptions(_companyId: string): Promise<FilterOptions> {
    const response = await client<FilterOptions>({
      method: 'get',
      url: makeApiUrl('/management/options')
    })

    return formatFilterOptions(response.body?.data)
  },

  /**
   * Send bulk welcome/access emails
   * OLD ENDPOINT: POST /api/user/send-welcome-email
   *
   * NOTE: Old backoffice only sends access emails (not welcome)
   * Receives array of email addresses, filters by onboardingComplete=false and active=1
   */
  async sendBulkEmail(emailList: string[]): Promise<{ success: boolean; message: string }> {
    const response = await client<{ success: boolean; message: string }>({
      method: 'post',
      url: makeApiUrl('/user/send-welcome-email'),
      body: emailList
    })
    return response.body.data as { success: boolean; message: string }
  },

  /**
   * Resend access email to individual user
   * OLD ENDPOINT: POST /api/user/resendAccess/{userId}
   */
  async resendAccess(userId: string): Promise<void> {
    await client<void>({
      method: 'post',
      url: makeApiUrl(`/user/resendAccess/${userId}`)
    })
  }
})

export const collaboratorService = createCollaboratorService()
